import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Clock, Check, FileText, ShoppingCart, ArrowLeft } from 'lucide-react';
import { contracts, contractCategories } from '../../data/contracts';
import { useCart } from '../../context/CartContext';
import { toast } from 'react-hot-toast';

const ContractDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addItem } = useCart();
  
  const contract = contracts.find(c => c.id === id);
  
  if (!contract) {
    return (
      <div className="container mx-auto px-4 py-8 text-center mt-8">
        <h1 className="text-2xl font-bold mb-4">Contract niet gevonden</h1>
        <p className="mb-6">Het contract dat u zoekt bestaat niet of is verwijderd.</p>
        <button
          onClick={() => navigate('/contracts')}
          className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-300"
        >
          Terug naar catalogus
        </button>
      </div>
    );
  }
  
  const category = contractCategories.find(c => c.id === contract.category);
  
  const handleAddToCart = () => {
    addItem(contract);
    toast.success('Contract toegevoegd aan winkelwagen');
  };
  
  return (
    <div className="container mx-auto px-4 py-8 mt-8">
      <button
        onClick={() => navigate('/contracts')}
        className="flex items-center text-blue-600 hover:text-blue-800 mb-6"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Terug naar catalogus
      </button>
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:justify-between md:items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">{contract.name}</h1>
              <div className="flex items-center mb-4">
                <span className="bg-blue-100 text-blue-800 text-sm font-semibold px-2.5 py-0.5 rounded">
                  {category?.name}
                </span>
              </div>
            </div>
            
            <div className="mt-4 md:mt-0 md:text-right">
              <div className="text-3xl font-bold text-gray-800 mb-2">
                {contract.price.min === contract.price.max 
                  ? `${contract.price.min} €`
                  : `${contract.price.min} - ${contract.price.max} €`
                }
              </div>
              <div className="flex items-center text-gray-600 justify-start md:justify-end">
                <Clock className="h-4 w-4 mr-2" />
                {contract.processingTime}
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Beschrijving</h2>
            <p className="text-gray-700 mb-6">{contract.description}</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg font-semibold mb-3">Belangrijkste punten</h3>
                <ul className="space-y-2">
                  {contract.keyPoints.map((point, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-3">Gebruikssituaties</h3>
                <ul className="space-y-2">
                  {contract.useCases.map((useCase, index) => (
                    <li key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{useCase}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Gerelateerde documenten</h2>
            <div className="space-y-2">
              {contract.relatedDocuments.map((document, index) => (
                <div key={index} className="flex items-center text-gray-700">
                  <FileText className="h-5 w-5 text-blue-500 mr-2" />
                  {document}
                </div>
              ))}
            </div>
          </div>
          
          <div className="border-t border-gray-200 pt-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <p className="text-gray-600">
                  Heeft u meer informatie nodig? Neem contact met ons op.
                </p>
              </div>
              
              <div className="flex space-x-4">
                <button
                  onClick={handleAddToCart}
                  className="flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition duration-300"
                >
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  Toevoegen aan winkelwagen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContractDetails;